import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import history from '../../history';
import {Routes} from '../../routes/routes';
import {ReduxState} from '../../store/types';
import {actions} from '../../store';
import Config from '../../config.json';
import {AccountPermission} from '../../store/types/Permission';
import {useRawPermissions} from '../../hooks/usePermissions';
import {useExtensionDetailsForm} from '../../hooks/useExtensionDetailsForm';
import usePageTitle from '../../hooks/usePageTitle';
import {getTabNumber} from '../../utils/getTabNumber';
import {generateTabs} from '../../utils/generateTabs';
import {convertBool} from '../../utils/parseBool';
import Loader from '../../components/Loader/Loader';
import DetailsWrapper from '../../components/DetailsWraper/DetailsWrapper';
import PermissionProvider from '../../components/PermissionProvider/PermissionProvider';
import ErrorNotification from '../../components/TabErrors/ErrorNotification';
import NotFound from '../NotFound/NotFound';
import {DetailsProps, EditExtensionForm, ExtensionDetailsPayload, useStyles} from './utils';
import CallScreeningTab from '../../components/Tabs/CallScreeningTab';
import CallForwardingTab from '../../components/Tabs/CallForwardingTab';
import {MainProductType} from '../../components/Extensions/MainProductSelectFiled/utils';
import {indexOf} from 'lodash';
import {EXTENSION_NOT_FOUND_BY_ID_API_ERROR} from '../../store/sagas/extensions/extensions/saga';
import {TabInterface} from "../../components/Tabs/Tabs.utils";
import ExtensionTab from '../Extensions/ExtensionDetails/ExtensionTab';
import CallSettingsTab from '../AutoAttendants/Details/CallSettingsTab';
import PlanTab from '../Extensions/ExtensionDetails/PlanTab';
import DeviceTab from '../Extensions/ExtensionDetails/DeviceTab';
import ExtensionDetailsHeader from '../Extensions/ExtensionDetails/ExtensionDetailsHeader';

const AccountExtension: React.VFC<DetailsProps> = (
    {
        actionButtonVisible,
    }) => {
    usePageTitle();
    const classes = useStyles();
    const dispatch = useDispatch();
    const {t} = useTranslation();

    const {
        i_account,
        extension_id,
        i_c_ext,
        i_customer,
        account_id,
        um_domain
    } = useExtensionDetailsForm();

    const saveClicked = useSelector(
        (state: ReduxState) => state.tabs?.saveForms || false,
    );

    const [tabIndex, setTabIndex] = useState(
        getTabNumber([0, 1, 2, 3, 4, 5, 6, 7]),
    );

    const [allTabsLoaded, setAllTabsLoaded] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [saveEnabled, setSaveEnabled] = useState(false);

    const formData = useRef<ExtensionDetailsPayload>();
    const permissions = useRawPermissions();

    const formsSubmitFuncRef = useRef<
        { [key in string]: (() => void) | undefined }
    >({
        details: undefined,
        callSettings: undefined,
        ai:undefined,
        callScreening: undefined,
        callForwarding: undefined,
        plan: undefined,
        device: undefined,
    });

    const formsErrorsValidationFuncRef = useRef<
        { [key in string]: (() => boolean) | undefined }
    >({
        details: undefined,
        callSettings: undefined,
        ai:undefined,
        callScreening: undefined,
        callForwarding: undefined,
        plan: undefined,
        device: undefined,
    });

    const formsIsDirtyFuncRef = useRef<{ [key in string]: boolean }>({
        details: false,
        callSettings: false,
        ai:false,
        callScreening: false,
        callForwarding: false,
        plan: false,
        device: false,
    });

    const extensionDetails = useSelector(
        (state: ReduxState) => state.extensions,
    );

    const {
        isFormSending,
        isFormDataLoading,
        isExtensionDeleted,
    } = useSelector((state: ReduxState) => state.extensions);


    const setSubmitFunc = useCallback((
        funcName: keyof typeof formsSubmitFuncRef.current,
        func: () => void,
    ) => {
        formsSubmitFuncRef.current[funcName] = func;
    }, []);

    const setFormIsValidFunc = useCallback((
        funcName: keyof typeof formsErrorsValidationFuncRef.current,
        func: () => boolean,
    ) => {
        formsErrorsValidationFuncRef.current[funcName] = func;
    }, []);

    useEffect(() => {
        if (!isFormDataLoading && !i_account) {
            dispatch(
                actions.getAccountExtensionData.request(),
            );
        }

        if (i_account && i_customer) {
            dispatch(
                actions.getAccountExtensionTabData.request({
                    i_account,
                    i_c_ext: i_c_ext ?? 0,
                    i_customer,
                }),
            );

            dispatch(
                actions.getCallScreeningTabData.request({
                    i_account,
                    i_customer,
                }),
            );

            dispatch(actions.getCallForwardingTabData.request({i_account}));

            dispatch(actions.getPlanTabData.request({i_account}));

            dispatch(actions.getDeviceTabData.request({i_account}));

        }

    }, [isFormDataLoading, i_account]);

    useEffect(() => {
        let loadingInProgress = false;

        if (isFormDataLoading) {
            loadingInProgress = true;
        } else if (extensionDetails) {
            Object.keys(extensionDetails).forEach((atr: string) => {
                if (
                    // @ts-ignore
                    extensionDetails[atr] &&
                    // @ts-ignore
                    extensionDetails[atr]?.isLoading === true
                ) {
                    loadingInProgress = true;
                }
            });

            if (!loadingInProgress && !isFormDataLoading) {
                setAllTabsLoaded(true);
            }
        }
    }, [extensionDetails]);

    useEffect(() => {
        if (allTabsLoaded) {
formData.current = {
                init: {...formData.current?.updated},
                updated: {...formData.current?.updated},
            };
        }
    }, [allTabsLoaded]);

    useEffect(() => {
        (allTabsLoaded && isDirty) || actionButtonVisible
            ? setSaveEnabled(true)
            : setSaveEnabled(false);
    }, [allTabsLoaded, isDirty]);

    useEffect(() => {
        if (isExtensionDeleted) {
            setTimeout(() => {
                history.push(Routes.Extensions);
            }, 1000);
        }
    }, [isExtensionDeleted]);

    const clearDetails = () => {
        history.push(Routes.AccountDashboard);
    };

    const handleDataFormUpdate = useCallback((form: Partial<EditExtensionForm>) => {
formData.current = {
            init: {...formData.current?.init},
            updated: {...formData.current?.updated, ...form},
        };
    }, [formData]);

    const handleDataInitFormUpdate = useCallback((form: Partial<EditExtensionForm>) => {
        formData.current = {
            init: {...formData.current?.init, ...form},
            updated: {...formData.current?.updated},
        };
    }, [formData]);

    const submitAllForms = useCallback(() => {
        Object.keys(formsSubmitFuncRef.current).forEach((key) => {
            formsSubmitFuncRef.current?.[key]?.();
        });
    }, [formsSubmitFuncRef]);

    const markTabAsHaveError = (tabName: string, tabIndex: number, setTabError: boolean) => {
        dispatch(actions.setErrorInTab({name: tabName, index: tabIndex, markAsInvalid: setTabError}))
    }

    const areAllFormsValid = useCallback(() => {
        let formsAreValid = true;
        Object.keys(formsErrorsValidationFuncRef.current).forEach((key) => {
            let setTabError;

            if (
                formsErrorsValidationFuncRef.current[key] !== undefined &&
                !formsErrorsValidationFuncRef.current[key]?.()
            ) {
                formsAreValid = false;
                setTabError = true;
            } else {
                setTabError = false
            }
            // @ts-ignore
            markTabAsHaveError(tabsNameResolver[key], tabNames.indexOf(tabsNameResolver[key]), setTabError);

        });


        return formsAreValid;
    }, [formsErrorsValidationFuncRef]);

    const onSubmitExtensionDetails = useCallback((blockRedirection?: boolean) => {
        submitAllForms();

        let formsAreValid = true;
        setTimeout(() => {
            formsAreValid = areAllFormsValid();
        }, 0);

        setTimeout(() => {
            if (formsAreValid) {

                setTimeout(() => {
                    if (formData.current) {
                        const {init, updated} = formData.current;
                        if (i_account && account_id) {
                            dispatch(
                                actions.editExtension.request({
                                    accountId: i_account,
                                    // @ts-ignore
                                    initialValues: init,
                                    // @ts-ignore
                                    changedValues: updated,
                                    redirectTab: tabIndex,
                                    id: account_id,
                                    domain: um_domain || '',
                                    blockRedirection: blockRedirection,
                                }),
                            );
                        }
                    }
                    dispatch(actions.saveForms({visible: false}))
                }, 0);
            } else {
                dispatch(actions.saveForms({visible: true}))
            }
        }, 1000);
    }, [formData, i_c_ext, i_account, account_id, tabIndex]);

    const checkAllDirtyState = useCallback(() => {
        let isAnyFormDirty = false;

        Object.keys(formsIsDirtyFuncRef.current).forEach((key) => {
            if (formsIsDirtyFuncRef.current[key]) {
                isAnyFormDirty = true;
            }
        });

        isAnyFormDirty ? setIsDirty(true) : setIsDirty(false);
    }, [formsIsDirtyFuncRef]);

    const setIsDirtyFunc = (
        funcName: keyof typeof formsSubmitFuncRef.current,
        isDirty: boolean,
    ) => {
        formsIsDirtyFuncRef.current[funcName] = isDirty;
        checkAllDirtyState();
    };

    // Memoize tab props to keep references stable
    const tabProps = useMemo(() => ({
        handleSubmitForm: handleDataFormUpdate,
        handleInitDataChange: handleDataInitFormUpdate,
        handleDirtyChange: setIsDirtyFunc,
        handleSetSubmitFunc: setSubmitFunc,
        handleSetIsValidFunc: setFormIsValidFunc,
    } as TabInterface), [
        handleDataFormUpdate,
        handleDataInitFormUpdate,
        setIsDirtyFunc,
        setSubmitFunc,
        setFormIsValidFunc,
    ]);


    const tabsNameResolver = {
        details: t('screens:extensions.extension'),
        callSettings: t('screens:extensions.callSettings'),
        callForwarding: t('screens:extensions.callForwarding'),
        plan: t('screens:extensions.plan'),
        device: t('screens:devices.device'),
        callScreening: t('screens:extensions.callScreening'),
    }

    const {tabNames, tabs, perm, tabsIcons} = useMemo(() => generateTabs(
        [
            {
                title: t('screens:extensions.extension'),
                permission: AccountPermission.MyExtension.Extension.value,
                tab: (
                    <ExtensionTab
                        {...tabProps}
                        key={'tab1'}
                        prefixEnabled={convertBool(Config.PREFIX_DID_ENABLED) || false}
                        isActive={tabIndex == 0}
                        tabName={t('screens:extensions.extension')}
                        callback={() => {
                            markTabAsHaveError(t('screens:extensions.extension'), indexOf(tabNames, t('screens:extensions.extension')), true);
                            dispatch(actions.saveForms({visible: true}));
                        }}
                    />
                ),
            },
            {
                title: t('screens:extensions.callSettings'),
                permission: AccountPermission.MyExtension.CallSettings.value,
                tab: <CallSettingsTab
                    accountId={i_account}
                    {...tabProps}
                    key={'tab2'}
                    isActive={tabIndex == 1}
                    tabName={t('screens:extensions.callSettings')}

                />,
            },
            {
                title: t('screens:extensions.callScreening'),
                permission: AccountPermission.MyExtension.CallScreening.value,
                tab: (
                    <CallScreeningTab
                        {...tabProps}
                        extensionNumber={extension_id}
                        key={'tab4'}
                        isActive={tabIndex == 3}
                        tabName={t('screens:extensions.callScreening')}
                    />
                ),
            },
            {
                title: t('screens:extensions.callForwarding'),
                permission: AccountPermission.MyExtension.CallForwarding.value,
                tab: <CallForwardingTab
                    {...tabProps} key={'tab5'}
                    isActive={tabIndex == 4}
                    tabName={t('screens:extensions.callForwarding')}
                />,
            },
            {
                title: t('screens:extensions.plan'),
                permission: AccountPermission.MyExtension.Plan.value,
                tab: (
                    <PlanTab
                        {...tabProps}
                        productType={MainProductType.Extensions}
                        key={'tab6'}
                        isActive={tabIndex == 5}
                        tabName={t('screens:extensions.plan')}
                    />
                ),
            },
            {
                title: t('screens:devices.device'),
                permission: AccountPermission.MyExtension.Device.value,
                tab: (
                    <DeviceTab
                        {...tabProps}
                        key={'tab7'}
                        assignPermission={
                            AccountPermission.MyExtension.Device.value
                        }
                        releasePermission={
                            AccountPermission.MyExtension.Device.value
                        }
                        changePermission={
                            AccountPermission.MyExtension.Device.value
                        }
                        isActive={tabIndex == 6}
                        tabName={t('screens:devices.device')}

                    />
                ),
            },
        ].filter(el => !Config.VOICE_AI_ADAPTER_URL.length ? el.title !== 'AI agent' : true),
        permissions
    ), [i_account, permissions]);


    const onTabChange = (_: React.ChangeEvent<{}>, index: any) => {
        areAllFormsValid();
        dispatch(actions.saveForms({visible: false}))
        setTabIndex(index);
    }

    if (extensionDetails?.extensionTabReducerApiError?.faultcode === EXTENSION_NOT_FOUND_BY_ID_API_ERROR) {
        return (
            <NotFound/>
        );
    }

    if (isFormDataLoading) {
        return (
            <div className={classes.loader}>
                <Loader dataQa="extension-list-loader"/>
            </div>
        );
    }

    return (
        <>
            <DetailsWrapper
                showActionButtons
                top={
                    <PermissionProvider
                        permission={
                            AccountPermission.MyExtension.value
                        }
                    >
                        <ExtensionDetailsHeader/>
                    </PermissionProvider>
                }
                tabs={tabs}
                saveEnable={saveEnabled}
                tabsIcons={tabsIcons}
                preventIsOpen={isDirty}
                preventSavePress={() => onSubmitExtensionDetails(true)}
                onTabChange={onTabChange}
                tabsNames={tabNames}
                defaultTabIndex={tabIndex}
                onSavePress={() => onSubmitExtensionDetails(false)}
                onBackPress={clearDetails}
                deleteEnable={false}
                perm={perm}
                actionsCustomClass={classes.actionsPanel}
                headerCustomClass={classes.headerAccountContainer}
            >
                {isFormSending && (
                    <Loader
                        dataQa="extension-form-data-sending-loader"
                        absolutePosition
                        dataTestId={'send-data-loader'}
                    />
                )}
                <ErrorNotification tabName={tabNames[tabIndex]} isSubmitting={saveClicked}/>
            </DetailsWrapper>
        </>
    );
};

export default AccountExtension;
